import { StateCreator } from "zustand";
import { HeatedSegment } from "./properties.ts";
import { ESRLogin } from "../../../ESR-API/login.ts";
import { ESRFinancialPlan } from "../../../ESR-API/financial_plan.ts";
import { ESRProfitabilityCalculation } from "../../../ESR-API/profitability_calculation.ts";
import { ESRBasicData } from "../../../ESR-API/basic_data.ts";
import { ESRCheckZip } from "../../../ESR-API/check_zip.ts";
import {
  ESRCalculateEnergyCosts,
  ESRCalculateEnergyCostsBody,
} from "../../../ESR-API/calculate_energy_costs.ts";
import { ESRGetCosts } from "../../../ESR-API/get_costs.ts";

import {
  FDWAntragsteller,
  FDWResult,
  FDWResultEntry,
  FDWResultResponse,
} from "../../../restfd2/fdw_result.ts";
import { FDWGid, Gemeinde } from "../../../restfd2/fdw_gid.ts";
import { FDWSession } from "../../../restfd2/fdw_session.ts";
// import { FDWTextblock, Textblock } from "../../../restfd2/fdw_textblock.ts";
// import { FDWFp, FDWFpResponse} from "../../../restfd2/fdw_fp.ts";
// import { FDWPdfLink} from "../../../restfd2/fdw_pdf_link.ts";
// import { FDWStatAccesstime} from "../../../restfd2/fdw_stat_accesstime.ts";
// import { FDWSendFormdata} from "../../../restfd2/fdw_send_formdata.ts";
// import { FDWDoiSendMail} from "../../../restfd2/fdw_doi_sendmail.ts";
// import { FDWDoiCheckStatus} from "../../../restfd2/fdw_doi_checkstatus.ts";
// import { FDWStatItem} from "../../../restfd2/fdw_stat_item.ts";
// import { Evu, FDWEvuid } from "../../../restfd2/fdw_evuid.ts";

import {
  BuildingTypeKeys,
  ESRGetTradeMeasures,
  ESRGetTradeMeasuresResponse,
} from "../../../ESR-API/get_trade_measure.ts";
import { PropertySlice } from "./property-slice.ts";
import { AppSlice } from "./app-slice.ts";
import { round_to_01, round_to_10 } from "../functions.ts";

//const LIVE : boolean = true;
// const DEBUG_API_USER = "febis";
// const DEBUG_API_PASSWORD = "L6g$ds71Us%5";
//  const BASEURL = "https://esr.fe-bis.de/de/main";

const zip = (a, b, c) => a.map((k, i) => [k, b[i], c[i]]);

let menu_timeout: number | undefined = undefined;

type MeasureKey =
  | "facade_insulation"
  | "floor_insulation"
  | "heating_system"
  | "photovoltaics"
  | "roof_insulation"
  | "solar_thermal"
  | "ventilation_system"
  | "window";
type MeasureState = "current_state" | "planned_state";
interface MeasureFilters {
  facade_insulation: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  floor_insulation: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  heating_system: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  photovoltaics: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  roof_insulation: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  solar_thermal: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  ventilation_system: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
  window: {
    current_state: () => boolean;
    planned_state: () => boolean;
  };
}

export interface FinancialPlanItem {
  subsidy: number;
  total_costs: number;
  planned_costs: number;
}
export interface FinancialPlan {
  window: FinancialPlanItem;
  photovoltaics: FinancialPlanItem;
  solar_thermal: FinancialPlanItem;
  heating_system: FinancialPlanItem;
  roof_insulation: FinancialPlanItem;
  floor_insulation: FinancialPlanItem;
  facade_insulation: FinancialPlanItem;
  ventilation_system: FinancialPlanItem;
}
export interface ApiSlice {
  base_url?: string;
  session_token?: string;
  questionizer_session_token?: string;
  //original_measure_labels store the measure_lables as loaded from api. It can be used to restore measure_labels if they are modified
  original_measure_lables: Record<string, string>;
  measure_lables: Record<string, string>;
  trade_measures: ESRGetTradeMeasuresResponse;
  pv?: {
    area: number;
    hint: string;
    elec_annual_savings: number;
    elec_self_consumption: number;
  };
  pv_hint?: string;
  // pv_area?: number,
  // pv_elec_annual_savings?: number,
  // pv_elec_self_consumption?: number,
  payback_period?: number;
  heating_demand_kwh?: number;
  electricity_consumption_kwh?: number;
  annual_energy_consumption_kwh_adjusted?: number;
  annual_energy_consumption_kwh_default?: number;
  annual_energy_consumption_kwh_planned?: number;
  annual_heating_costs_adjusted?: number;
  annual_heating_costs_default?: number;
  annual_heating_costs_planned?: number;
  annual_pv_production_kwh_adjusted?: number;
  annual_pv_production_kwh_default?: number;
  annual_pv_production_kwh_planned?: number;
  energy_efficiency_class_adjusted?: number;
  energy_efficiency_class_adjusted_level?: number;
  energy_efficiency_class_default?: number;
  energy_efficiency_class_default_level?: number;
  energy_efficiency_class_planned?: number;
  energy_efficiency_class_planned_level?: number;
  co2_tax_savings?: number;
  total_beg_subsidy?: number;
  savings_percentage?: number;
  co2_emission_current?: number;
  co2_emission_planned?: number;
  savings_over_lifetime?: number;
  total_investment_costs?: number;
  monthly_possible_savings?: number;
  period_under_review_savings?: number;
  period_under_review_energy_savings?: number;
  annual_savings_on_energy_costs?: number;
  energy_efficiency_class_current?: number;
  annual_energy_consumption_kwh_current?: number;
  annual_profit_loss?: number;
  monthly_profit_loss?: number;
  return_on_investment?: number;
  heizung_isfp?: boolean;
  keller_isfp?: boolean;
  dach_isfp?: boolean;
  fenster_isfp?: boolean;
  fassade_isfp?: boolean;
  solarthermie_isfp?: boolean;
  photovoltaik_isfp?: boolean;
  lueftung_isfp?: boolean;
  financial_plan: FinancialPlan;
  financial_plan_dummy: FinancialPlan & { version: number };
  total_final_costs: number;
  total_planned_costs: number;
  total_possible_subsidy: number;
  building_system: {
    costs: number;
    subsidy: number;
    percentage: number;
    max_funding: number;
  };
  building_envelope: {
    isfp: boolean;
    costs: number;
    subsidy: number;
    percentage: number;
    max_funding: number;
  };
  region_name: string;
  heizung_tmp: string;
  measures_filters: MeasureFilters;
  gemeinde_liste?: Array<Gemeinde>;
  gemeinde?: Gemeinde;
  gemeinde_id?: number;
  fundings_list?: FDWResultResponse;
  include_in_print: Record<string, boolean>;
  questionizer_open_print_view: (exclusive_id?: number) => Promise<void>;
  set_include_in_print: (id: number, value: boolean) => void;
  api_login: (base_url?: string) => Promise<void>;
  questionizer_login: () => Promise<void>;
  questionizer_request_fundings: () => Promise<void>;
  questionizer_request_gid: () => Promise<void>;
  api_basic_data: () => Promise<void>;
  api_check_zip: (plz: string) => Promise<boolean>;
  api_calculate_energy_costs: () => Promise<void>;
  api_calculate_energy_costs_dummy: () => Promise<void>;
  api_profitability_calculation: () => Promise<void>;
  api_financial_plan: () => Promise<void>;
  api_financial_plan_dummy: () => Promise<void>;
  api_recalculate: () => Promise<void>;
  api_get_costs: (type: string, code: string) => Promise<void>;
  set_measure_labels: (labels: string, key: string) => void;
  set_measure_filter: (
    key: MeasureKey,
    state: MeasureState,
    filter: () => boolean
  ) => void;
  get_measure_filter: (key: MeasureKey, state: MeasureState) => () => boolean;
}

export const createApiSlice: StateCreator<
  AppSlice & ApiSlice & PropertySlice,
  [],
  [],
  AppSlice
> = (set, get) => ({
  include_in_print: {},
  measures_filter: {
    facade_insulation: {
      current_state: () => true,
      planned_state: () => true,
    },
    floor_insulation: {
      current_state: () => true,
      planned_state: () => true,
    },
    heating_system: {
      current_state: () => true,
      planned_state: () => true,
    },
    photovoltaics: {
      current_state: () => true,
      planned_state: () => true,
    },
    roof_insulation: {
      current_state: () => true,
      planned_state: () => true,
    },
    solar_thermal: {
      current_state: () => true,
      planned_state: () => true,
    },
    ventilation_system: {
      current_state: () => true,
      planned_state: () => true,
    },
    window: {
      current_state: () => true,
      planned_state: () => true,
    },
  },
  get_measure_filter: (key, state) => {
    return get().measures_filter[key][state];
  },
  set_measure_filter: (key, state, filter) => {
    let measures_filter = { ...get().measures_filter };
    measures_filter[key][state] = filter;
    set((_) => ({
      measures_filter,
    }));
  },
  api_login: async (base_url) => {
    let session_token;

    console.log("API_LOGIN", base_url, get().base_url);
    if (!get().base_url && base_url) {
      set((_) => ({ base_url }));
    }

    if (get().config && get().config["development_mode"]) {
      const DEBUG_API_USER = "febis";
      const DEBUG_API_PASSWORD = "L6g$ds71Us%5";
      
      const request_login = new ESRLogin(DEBUG_API_USER, DEBUG_API_PASSWORD);
      const response_login = await request_login.request().catch((e) => {
        console.error("LOGIN ERROR:", e);
      });
      session_token = response_login.session_token;
    } else {
      const json = await (
        await fetch((base_url || get().base_url || "/") + "/session.php")
      ).json();
      if (json === undefined) {
        return;
      }
      session_token = json.session_token;
    }

    if (session_token === undefined) {
      return;
    }

    set((_) => ({
      session_token,
    }));

    //on_session_token();

    const request_measures = new ESRGetTradeMeasures(session_token);
    const response_measures = await request_measures.request().catch((e) => {
      console.error(e);
    });

    if (!response_measures) {
      return;
    }

    const measure_lables = Object.keys(response_measures.measures)
      .flatMap((m) => [
        ...Object.keys(response_measures.measures[m].options.current_state).map(
          (n) => {
            const x = response_measures.measures[m].options.current_state[n];
            return {
              [x.code]: x.label,
            };
          }
        ),
        ...Object.keys(response_measures.measures[m].options.planned_state).map(
          (n) => {
            const x = response_measures.measures[m].options.planned_state[n];
            return {
              [x.code]: x.label,
            };
          }
        ),
      ])
      .reduce((acc, x) => ({ ...acc, ...x }), {});

    set((_) => ({
      trade_measures: response_measures,
      measure_lables,
      original_measure_lables: measure_lables,
    }));
  },

  questionizer_login: async () => {
    const config = get().config;
    if (!config) {
      console.warn("Abbruch: config ist undefined in requestSessionId()");
      return;
    }

    console.log("request new session_id");

    const mode = get().config!.mode;
    const session_file = ".php";

    if (get().config && get().config["development_mode"]) {
      const session_request = new FDWSession(
        "hornbach_at_fd_ws",
        "mKT2maui45BT"
      );
      const session = await session_request.request().catch((e) => {
        console.error(e);
      });
      console.log("login:", session);
      set((_) => ({
        questionizer_session_token: session!.session_id,
      }));
    } else {
      const session = await (
        await fetch((get().base_url || "/") + "/session_questionizer.php")
      ).json();

      if (session) {
        set((_) => ({
          questionizer_session_token: session!.session_id,
        }));
      } else {
        console.error("live mode failed loading session");
      }
    }
  },

  questionizer_request_gid: async () => {
    try {
      const fdw_gid_request = new FDWGid(
        get().questionizer_session_token,
        get().postleitzahl
      );
      const result = await fdw_gid_request.request();

      if (!result || result.length == 0) {
        set((_) => ({
          gemeinde_id: undefined,
          gemeinde: undefined,
          gemeinde_liste: undefined,
        }));
        get().updateValidation();
        return;
      }

      if (result.length == 1) {
        set((_) => ({
          gemeinde_id: result[0].gemeinde_id,
          gemeinde: result[0],
          gemeinde_liste: undefined,
        }));
      } else {
        set((_) => ({
          gemeinde_id: undefined,
          gemeinde: undefined,
          gemeinde_liste: result,
        }));
      }
    } catch (error) {
      set((_) => ({
        gemeinde_id: undefined,
        gemeinde: undefined,
        gemeinde_liste: undefined,
      }));
      console.error(error as Error);
    }
  },

  questionizer_request_fundings: async () => {
    if (get().questionizer_session_token === undefined) {
      console.log("no session token");
      return;
    }

    const subsidy_measures_code = zip(
      [
        "facade_insulation",
        "floor_insulation",
        "heating_system",
        "photovoltaics",
        "roof_insulation",
        "solar_thermal",
        "ventilation_system",
        "window",
      ],
      [
        "fassade",
        "keller",
        "heizung",
        "photovoltaik",
        "dach",
        "solarthermie",
        "lueftung",
        "fenster",
      ],
      [
        get().fassade_future,
        get().keller_future,
        get().heizung_future,
        get().photovoltaik_future,
        get().dach_future,
        get().solarthermie_future,
        get().lueftung_future,
        get().fenster_future,
      ]
    )
      .filter(([_a, _b, mask]) => mask)
      .map(
        ([key, prop, _]) =>
          get().trade_measures["measures"][key]["options"]["planned_state"].map(
            (m) => JSON.parse(m["subsidy_measures_code"])
          )[1]
      )
      .reduce((acc, x) => ({ ...acc, ...x }), {});

    console.log("subsidy_measures_code", subsidy_measures_code);

    try {
      const _antragsteller =
        get().antragsteller !== undefined && get().antragsteller?.length == 1
          ? get().antragsteller![0]
          : get().antragsteller_detail;
      const request = new FDWResult(
        get().questionizer_session_token!,
        get().gemeinde_id,
        subsidy_measures_code,
        "Altbau",
        true,
        true,
        false,
        "",
        get().baujahr,
        "",
        9,
        undefined,
        undefined,
        "de",
        undefined
      );
      const result = await request.request();
      console.log("funduing list", result);
      set((_) => ({
        fundings_list: result,
      }));
    } catch (error) {
      console.error(error);
      set((_) => ({
        fundings_list: undefined,
      }));
    }
  },

  set_measure_labels: async (labels, key) => {
    set((_) => ({
      measure_lables: (get().measure_lables[key] = labels),
    }));
  },

  api_calculate_energy_costs: async () => {
    const session_token = get().session_token;
    if (session_token == undefined) {
      return;
    }

    function remove_undefined_from_object(obj: ESRCalculateEnergyCostsBody) {
      Object.keys(obj).forEach((measure) => {
        ["current_state", "planned_state"].forEach((a) => {
          [
            "code",
            "income_bonus",
            "replacement",
            "cost_estimate",
            "installation_year",
            "isfp",
            "natural_refrigerant",
            "heizung_natural_refrigerant",
            "heizung_natural_refrigerant_future",
            "annual_yield_kWh",
            "roof_pitch",
            "orientation",
            "battery_storage",
          ].forEach((b) => {
            if (
              obj[measure][a][b] === undefined ||
              obj[measure][a][b] === "" ||
              obj[measure][a][b] === null ||
              obj[measure][a][b] === 0
            ) {
              delete obj[measure][a][b];
            }
          });

          if (
            obj[measure][a] !== undefined &&
            Object.keys(obj[measure][a]).length == 0
          ) {
            delete obj[measure][a];
          }
        });

        if (Object.keys(obj[measure]).length == 0) {
          delete obj[measure];
        }
      });
    }

    const renovation_measures = {
      facade_insulation: {
        current_state: {
          code: get().fassade,
          installation_year: get().fassade_datum,
        },
        planned_state: {
          code: get().fassade_future,
          cost_estimate: get().fassade_kosten,
          isfp: get().fassade_isfp,
        },
      },
      roof_insulation: {
        current_state: {
          code: get().dach,
          installation_year: get().dach_datum,
        },
        planned_state: {
          code: get().dach_future,
          cost_estimate: get().dach_kosten,
          isfp: get().dach_isfp,
        },
      },
      floor_insulation: {
        current_state: {
          code: get().keller,
          installation_year: get().keller_datum,
        },
        planned_state: {
          code: get().keller_future,
          cost_estimate: get().keller_kosten,
          isfp: get().keller_isfp,
        },
      },
      window: {
        current_state: {
          code: get().fenster,
          installation_year: get().fenster_datum,
        },
        planned_state: {
          code: get().fenster_future,
          cost_estimate: get().fenster_kosten,
          isfp: get().fenster_isfp,
        },
      },
      heating_system: {
        current_state: {
          code: get().heizung,
          installation_year: get().heizung_datum,
          natural_refrigerant: get().heizung_natural_refrigerant,
        },
        planned_state: {
          code: get().heizung_future,
          cost_estimate: get().heizung_kosten,
          natural_refrigerant: get().heizung_natural_refrigerant_future,
          isfp: get().heizung_isfp,
          income_bonus: get().income_gt_4k,
          replacement: get().heizung_wird_ausgetauscht,
          private_residential_property: get().privat,
          dust_emission: get().heizung_dust_emission,
          hot_water: get().hot_water,
        },
      },
      ventilation_system: {
        current_state: {
          code: get().lueftung,
          installation_year: get().lueftung_datum,
        },
        planned_state: {
          code: get().lueftung_future,
          cost_estimate: get().lueftung_kosten,
          isfp: get().lueftung_isfp,
        },
      },
      solar_thermal: {
        current_state: {
          code: get().solarthermie,
          installation_year: get().solarthermie_datum,
          roof_pitch: get().solarthermie_roof_pitch,
          collector_area: get().solarthermie_kollektorflaeche,
          orientation: get().solarthermie_orientation,
        },
        planned_state: {
          code: get().solarthermie_future,
          cost_estimate: get().solarthermie_kosten,
          roof_pitch: get().solarthermie_roof_pitch_future,
          orientation: get().solarthermie_orientation_future,
          collector_area: get().solarthermie_kollektorflaeche_future,
        },
      },
      photovoltaics: {
        current_state: {
          code: get().photovoltaik,
          installation_year: get().photovoltaik_datum,
          roof_pitch: get().photovoltaik_roof_pitch,
          orientation: get().photovoltaik_orientation,
          annual_yield_kWh: get().photovoltaik_annual_yield_kWh,
          battery_storage: get().photovoltaik_battery_storage,
        },
        planned_state: {
          code: get().photovoltaik_future,
          cost_estimate: get().photovoltaik_kosten,
          roof_pitch: get().photovoltaik_roof_pitch_future,
          orientation: get().photovoltaik_orientation_future,
          battery_storage: get().photovoltaik_battery_storage_future,
        },
      },
    };

    remove_undefined_from_object(renovation_measures);

    let heizung = get().heizung;
    let oelverbrauch = get().oelverbrauch / 0.1;
    let gasverbrauch = get().gasverbrauch;
    let stromverbrauchdiodenheizung = get().stromverbrauchdiodenheizung;
    let gasverbrauch_m3 = get().gasverbrauch_m3 / 0.0952;
    let holzverbrauch = get().holzverbrauch * 4.8;
    let holzverbrauch_t = get().holzverbrauch_t * 4800;
    let holzverbrauch_m3 = get().holzverbrauch_m3 * 3.12;
    let stromverbrauchwaermepumpe = get().stromverbrauchwaermepumpe;

    let verbrauch_kwh = 0;
    switch (heizung) {
      case "heating_system_current_0":
      case "heating_system_current_8":
      case "heating_system_current_10":
        verbrauch_kwh = oelverbrauch;
        break;
      case "heating_system_current_1":
      case "heating_system_current_7":
      case "heating_system_current_9":
        if (get().verbrauchseinheit_gas == "m3") {
          verbrauch_kwh = gasverbrauch_m3;
        } else {
          verbrauch_kwh = gasverbrauch;
        }
        break;
      case "heating_system_current_2":
        if (get().verbrauchseinheit_holz == "t") {
          verbrauch_kwh = holzverbrauch_t;
        } else if (get().verbrauchseinheit_holz == "m3") {
          verbrauch_kwh = holzverbrauch_m3;
        } else {
          verbrauch_kwh = holzverbrauch;
        }
        break;
      case "heating_system_current_3":
      case "heating_system_current_4":
        verbrauch_kwh = stromverbrauchwaermepumpe;
        break;
      case "heating_system_current_6":
        verbrauch_kwh = gasverbrauch;
      case "heating_system_current_5":
        verbrauch_kwh = stromverbrauchdiodenheizung;
        break;
    }

    const body = {
      annual_energy_consumption_kWh: verbrauch_kwh,
      annual_electricity_consumption_kWh: get().stromverbrauch,
      renovation_measures,
    };

    const request = new ESRCalculateEnergyCosts(session_token, body);
    const response = await request.request().catch((e) => {
      console.error(e);
    });

    if (response) {
      const rounded = Object.keys(response).reduce(
        (acc, key) => ({ ...acc, [key]: round_to_10(response[key] || 0) }),
        {}
      );
      set((_) => ({
        ...rounded,
        energy_efficiency_class_default_level:
          response.energy_efficiency_class_default_level,
        energy_efficiency_class_adjusted_level:
          response.energy_efficiency_class_adjusted_level,
        energy_efficiency_class_planned_level:
          response.energy_efficiency_class_planned_level,
      }));
    }
  },

  set_include_in_print: (id, value) => {
    const x = { ...get().include_in_print };
    x[id] = value;
    set((_state) => ({ include_in_print: x }));
  },

  questionizer_open_print_view: async (exclusive_id) => {
    const filter = [
      get().fassade_future,
      get().keller_future,
      get().heizung_future,
      get().photovoltaik_future,
      get().dach_future,
      get().solarthermie_future,
      get().lueftung_future,
      get().fenster_future,
    ];
    const subsidy_measures_code = zip(
      [
        "facade_insulation",
        "floor_insulation",
        "heating_system",
        "photovoltaics",
        "roof_insulation",
        "solar_thermal",
        "ventilation_system",
        "window",
      ],
      [
        "fassade",
        "keller",
        "heizung",
        "photovoltaik",
        "dach",
        "solarthermie",
        "lueftung",
        "fenster",
      ],
      filter
    )
      .filter(([_a, _b, mask]) => mask)
      .map(
        ([key, prop, _]) =>
          get().trade_measures["measures"][key]["options"]["planned_state"].map(
            (m) => JSON.parse(m["subsidy_measures_code"])
          )[1]
      )
      .reduce((acc, x) => ({ ...acc, ...x }), {});

    const gewerke_labels = [
      "fassade",
      "keller",
      "heizung",
      "photovoltaik",
      "dach",
      "solarthermie",
      "lueftung",
      "fenster",
    ].filter((l, i) => filter[i]);

    const angabedaten = {
      massnahmen: gewerke_labels.join(", "),
      antragsteller: 9,
      ort: get().postleitzahl + " " + get().gemeinde?.gemeinde_name,
      gebaeude: "",
      typ: "Altbau",
      baujahr: get().baujahr,
    };

    const data = {
      baseurl: get().base_url,
      coverurl: "",
      logourl: "",
      logo2url: "",
      sessionid: get().questionizer_session_token,
      fpids: Object.keys(get().include_in_print).join(","),
      gemeindeid: get().gemeinde_id,
      gewerk: subsidy_measures_code,
      typ: "Altbau",
      wohngebaeude: true,
      gewerbegebaeude: true,
      gemeinnuetzig: false,
      gebaeude: 9,
      baujahr: get().baujahr,
      grundversorger: "",
      antragsteller: undefined,
      evu1id: undefined,
      evu2id: undefined,
      lang: "de",
      nutzer: "",
      inprint: exclusive_id
        ? exclusive_id
        : Object.keys(get().include_in_print)
            .filter((key) => get().include_in_print[key])
            .join(","),
      angabedaten: JSON.stringify(angabedaten),
    };

    //console.log("base_url", get().base_url, get().config?.base_asset_url, data);

    const windowOptions = "toolbar=0,location=0,menubar=0";

    const newWindow = window.open("", "questionizer", windowOptions);

    if (newWindow) {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "https://esr.carbacan-dev.de/printview/index.php";//get().config?.base_asset_url + "printview";
      form.target = "questionizer";

      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = data[key as keyof typeof data] as string;
          console.log(key, data[key], input);
          form.appendChild(input);
        }
      }

      console.log(data, form);
      document.body.appendChild(form);

      setTimeout(() => {
        form.submit(); // Sende das Formular
        document.body.removeChild(form); // Entferne das Formular nach dem Senden
      }, 100); // Ein kurzes Timeout, um sicherzustellen, dass das Fenster geöffnet ist
    } else {
      console.error(
        "Das neue Fenster konnte nicht geöffnet werden. Popup-Blocker aktiv?"
      );
    }
  },

  api_calculate_energy_costs_dummy: async () => {
    const session_token = get().session_token;
    if (session_token == undefined) {
      return;
    }

    const body = {
      annual_electricity_consumption_kWh: 3600,
      annual_energy_consumption_kWh: 3600,
      renovation_measures: {
        facade_insulation: {
          planned_state: {
            code: "facade_insulation_planned_1",
          },
        },
        floor_insulation: {
          planned_state: {
            code: "floor_insulation_planned_3",
          },
        },
        heating_system: {
          current_state: {
            code: "heating_system_current_0",
          },
          planned_state: {
            code: "heating_system_planned_2",
          },
        },
        photovoltaics: {
          planned_state: {
            code: "photovoltaics_planned_1",
          },
        },
        roof_insulation: {
          planned_state: {
            code: "roof_insulation_planned_5",
          },
        },
        solar_thermal: {
          current_state: {},
          planned_state: {
            code: "solar_thermal_planned_1",
            collector_area: "5",
          },
        },
        ventilation_system: {
          planned_state: {
            code: "ventilation_system_planned_2",
          },
        },
        window: {
          planned_state: {
            code: "window_planned_1",
          },
        },
      },
    };

    const request = new ESRCalculateEnergyCosts(session_token, body);
    const response = await request.request().catch((e) => {
      console.error(e);
    });

    if (response) {
    }
  },

  api_basic_data: async () => {
    const session_token = get().session_token;
    if (session_token == undefined) {
      return;
    }

    const request = new ESRBasicData(
      session_token,
      get().gebaeude_typ as BuildingTypeKeys,
      get().baujahr,
      get().postleitzahl,
      get().wohnflaeche,
      get().wohneinheiten,
      get().vollgeschosse,
      get().heizung,
      "DE",
      0, //(get().beheizte_segmente as HeatedSegment)[0],
      100, //(get().beheizte_segmente as HeatedSegment)[1],
      0, //(get().beheizte_segmente as HeatedSegment)[2],
      get().privat
    );
    const response = await request.request().catch((e) => {
      console.error(e);
    });
    if (response) {
      const heating_demand_kwh = response.heating_demand_kwh;
      const electricity_consumption_kwh = response.electricity_consumption_kwh;

      set((_) => ({
        heizung_tmp: get().heizung,
        ...response,
        oelverbrauch: round_to_10(heating_demand_kwh * 0.1),
        gasverbrauch: round_to_10(heating_demand_kwh),
        stromverbrauchdiodenheizung: round_to_10(heating_demand_kwh),
        gasverbrauch_m3: round_to_10(heating_demand_kwh * 0.0952),
        holzverbrauch: round_to_10(heating_demand_kwh / 4.8),
        holzverbrauch_t: round_to_01(heating_demand_kwh / 4800),
        holzverbrauch_m3: round_to_10(heating_demand_kwh / 3.12),
        stromverbrauchwaermepumpe: round_to_10(heating_demand_kwh),
        stromverbrauch: round_to_10(electricity_consumption_kwh),
      }));
    }
  },

  api_check_zip: async (plz : string) => {
    const session_token = get().session_token;
    if (session_token == undefined) {
      return;
    }

    if (plz === undefined || plz.length < 4 || plz.length > 5) {
      return;
    }

    const request = new ESRCheckZip(session_token, plz);

    const response = await request.request().catch((e) => {
      console.error(e);
    });

    set((_) => ({
      region_name: response.region_name,
      gemeinde_id: response,
    }));
    return response && response.region_name;
  },

  api_financial_plan: async () => {
    const request = new ESRFinancialPlan(get().session_token);
    const response = await request.request().catch((e) => {
      console.error(e);
    });
    if (response) {
      set((_) => ({ ...response }));
    }
  },

  api_financial_plan_dummy: async () => {
    await get().api_calculate_energy_costs_dummy();
    const request = new ESRFinancialPlan(get().session_token);
    const response = await request.request().catch((e) => {
      console.error(e);
    });
    if (response) {
      set((_) => ({
        financial_plan_dummy: { version: 1, ...response.financial_plan },
      }));
    }
  },

  api_profitability_calculation: async () => {
    const kostensteigerung = get().kostensteigerung_energie || 0;
    const co2_price = get().co2_price || 0;
    const request = new ESRProfitabilityCalculation(
      get().session_token,
      kostensteigerung,
      co2_price
    );
    const response = await request.request().catch((e) => {
      console.error(e);
    });
    if (response) {
      set((_) => ({ ...response }));
    }
  },

  api_recalculate: async () => {
    console.log("RECALCULATE");
    await get().api_calculate_energy_costs();
    await get().api_financial_plan();
    await get().api_profitability_calculation();
  },

  api_get_costs: async (type: string, code: string) => {
    const request = new ESRGetCosts(get().session_token, { code });
    const response = await request.request().catch((e) => {
      console.error(e);
    });

    if (!response) {
      return;
    }

    const financial_plan_dummy = { ...get().financial_plan_dummy };
    financial_plan_dummy.version += 1;
    financial_plan_dummy[type]["total_costs"] = response.costs;

    if (financial_plan_dummy) {
      //console.log(type, code, financial_plan_dummy);
      set((_) => ({ financial_plan_dummy }));
    }
  },
});
